/* eslint-disable no-restricted-syntax, no-param-reassign */
import _ from 'lodash';

import flattenKeys from '../../helpers/flatteKeys';

// "confirms" i.e. re-enter to confirm
// USAGE: define a "confirms" field with ui:options.confirms = $path
// where $path is the path for the property which is being confirmed
function validateConfirmationFields({ formData, errors, uiSchema, schema, t }) {
  // flatten the key paths
  // => 'path.to.field.ui:options.confirms'
  // pick the 'ui:options.confirms entries
  // drop the '.ui:options.confirms' suffix
  // => { 'path.to.field': 'path.to.confirm' }
  const confirmFields = _.mapKeys(
    _.pickBy(flattenKeys(uiSchema), (_v, k) => k?.endsWith('ui:options.confirms')),
    (_v, k) => k.replace('.ui:options.confirms', '')
  );
  for (const path of Object.keys(confirmFields)) {
    const expectingPath = confirmFields[path];
    const validatingValue = _.get(formData, path);
    const expectingValue = _.get(formData, expectingPath);
    if (validatingValue !== expectingValue) {
      _.get(errors, path)?.addError(t('errors.forms.match', { path: expectingPath }));
    }
  }

  return errors;
}

function validateFileSize({ formData, errors, uiSchema }) {
  // Check for all the keys having maxFileSize under ui:options
  const maxFileSizeFields = _.mapKeys(
    _.pickBy(flattenKeys(uiSchema), (_v, k) => k?.endsWith('ui:options.maxFileSize')),
    (_v, k) => k.replace('.ui:options.maxFileSize', '')
  );

  for (const path of Object.keys(maxFileSizeFields)) {
    const expectedSize = maxFileSizeFields[path];
    const filesArray = Array.wrap(_.get(formData, path));
    filesArray?.forEach((file, index) => {
      if (file) {
        const givenSize = atob(file.split(',')[1]).length;
        if (givenSize > expectedSize) {
          _.get(errors, path)?.addError(
            `File no: ${index + 1} size exceeds the maximum limit of ${expectedSize / 1024} KB. (${
              givenSize / 1024
            } KB)`
          );
        }
      }
    });
  }

  return errors;
}

function validateYear({ formData, errors, uiSchema, t }) {
  const yearFields = _.mapKeys(
    _.pickBy(
      flattenKeys(uiSchema),
      (_v, k) => k?.endsWith('ui:widget') && _.get(uiSchema, k.replace('.ui:widget', '')) === 'YearWidget'
    ),
    (_v, k) => k.replace('.ui:widget', '')
  );

  for (const path of Object.keys(yearFields)) {
    const yearValue = _.get(formData, path);

    if (yearValue) {
      const yearOptions = _.get(uiSchema, `${path}.ui:options`) || {};
      const minYear = yearOptions.minYear || 1600;
      const maxYear = yearOptions.maxYear || new Date().getFullYear();

      const hasNonDigits = /\D/.test(yearValue);
      if (hasNonDigits) {
        _.get(errors, path)?.addError(
          t('errors.forms.year_invalid_chars', { default: 'Year must contain only digits' })
        );
      } else if (yearValue.length !== 4) {
        _.get(errors, path)?.addError(t('errors.forms.year_length', { default: 'Year must be exactly 4 digits' }));
      } else {
        const yearValueInt = parseInt(yearValue, 10);
        if (yearValueInt < minYear || yearValueInt > maxYear) {
          _.get(errors, path)?.addError(
            t('errors.forms.year_range', {
              minYear,
              maxYear,
              default: `Year must be between ${minYear} and ${maxYear}`,
            })
          );
        }
      }
    }
  }

  return errors;
}

function validateCheckboxWithInputField({ formData, errors, uiSchema, t }) {
  const flattenedUiSchema = flattenKeys(uiSchema);
  const checkboxWithInputFields = _.mapKeys(
    _.pickBy(flattenedUiSchema, (value, key) => key?.endsWith('.ui:field') && value === 'checkbox_with_inputs'),
    (_value, key) => key.replace('.ui:field', '')
  );

  for (const path of Object.keys(checkboxWithInputFields)) {
    const selectedOptions = _.get(formData, path);

    for (const option of selectedOptions.selected) {
      const optionInput = selectedOptions[`${option}_input`];
      if (!optionInput || optionInput.length === 0) {
        const key = `${option}_input`;
        const rootErrors = _.get(errors, path);
        _.get(rootErrors, key)?.addError(t('errors.forms.required', { default: 'is required' }));
      }
    }
  }
  return errors;
}

function validatePhoneNumber({ formData, errors, uiSchema, t, schema }) {
  // Find all fields that use PhoneWidget
  const flattenedUiSchema = flattenKeys(uiSchema);

  const phoneFields = _.mapKeys(
    _.pickBy(flattenedUiSchema, (value, key) => key?.endsWith('.ui:widget') && value === 'PhoneWidget'),
    (_value, key) => key.replace('.ui:widget', '')
  );

  for (const path of Object.keys(phoneFields)) {
    const phoneValue = _.get(formData, path);
    const options = _.get(uiSchema, `${path}.ui:options`) || {};
    const requiredLength = options.length || 10;
    const digitsOnly = options.digitsOnly !== false;

    // Check if this field is required in the schema
    const pathParts = path.split('.');
    let currentSchema = schema;
    let isRequired = false;

    for (let i = 0; i < pathParts.length; i += 1) {
      const part = pathParts[i];
      if (currentSchema.properties && currentSchema.properties[part]) {
        if (i === pathParts.length - 1) {
          // Check if this field is in the required array
          isRequired = currentSchema.required && currentSchema.required.includes(part);
        }
        currentSchema = currentSchema.properties[part];
      }
    }

    if (phoneValue) {
      // If digitsOnly is true, check that it contains only digits
      if (digitsOnly) {
        const hasNonDigits = /\D/.test(phoneValue);
        if (hasNonDigits) {
          _.get(errors, path)?.addError(
            t('errors.forms.phone_invalid_chars', { default: 'Phone number must contain only digits' })
          );
        }
      }

      // Check length
      if (phoneValue.length !== requiredLength) {
        _.get(errors, path)?.addError(
          t('errors.forms.phone_length', {
            count: requiredLength,
            default: `Phone number must be exactly ${requiredLength} characters`,
          })
        );
      }
    } else if (isRequired) {
      // Only add required error if the field is actually required
      _.get(errors, path)?.addError(
        t('errors.forms.phone_required', {
          default: 'Please enter a phone number.',
        })
      );
    }
  }

  return errors;
}

function customValidateInner({ formData, errors, uiSchema, schema, t }) {
  errors = validateConfirmationFields({
    formData,
    errors,
    uiSchema,
    schema,
    t,
  });

  errors = validateCheckboxWithInputField({
    formData,
    errors,
    uiSchema,
    t,
  });

  errors = validateFileSize({
    formData,
    errors,
    uiSchema,
  });

  errors = validatePhoneNumber({
    formData,
    errors,
    uiSchema,
    t,
    schema,
  });

  errors = validateYear({
    formData,
    errors,
    uiSchema,
    t,
  });

  // we can add more custom validations here, call addError on the errors tree
  return errors;
}

export function makeCustomValidate(t, schema) {
  return function customValidate(formData, errors, uiSchema) {
    return customValidateInner({ formData, errors, uiSchema, schema, t });
  };
}
